/**
 * Iter XX.59 — Voice MVP: MediaRecorder wrapper + analytics beacon.
 *
 * Phase 1 is intentionally boring: record → stop → single Blob POST →
 * transcript → user confirms → existing chat send → TTS reply. No
 * streaming, no VAD, no interruption. That's exactly what we're
 * testing.
 */
import api from "./api";

// Ordered list — the first supported entry wins. Opus is the smallest
// wire format we can afford (~40 KB / 10 s on Chrome/Android). Safari
// falls through to `audio/mp4`, which Whisper accepts.
const CANDIDATE_MIMES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/mp4;codecs=mp4a.40.2",
];

/** True when this browser can record audio at all. */
export function isVoiceCapable() {
  return typeof window !== "undefined"
    && typeof window.MediaRecorder !== "undefined"
    && typeof navigator !== "undefined"
    && !!navigator.mediaDevices
    && typeof navigator.mediaDevices.getUserMedia === "function";
}

function _pickMime() {
  if (!isVoiceCapable()) return null;
  for (const m of CANDIDATE_MIMES) {
    try {
      if (window.MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* ignore */ }
  }
  return null;
}

/**
 * Fire an analytics event via the existing /plus/track beacon. Voice
 * analytics never carry transcript content — only latency, counts and
 * failure reasons. `payload` values are stringified.
 */
export function trackVoice(event, payload = {}) {
  try {
    const clean = {};
    Object.keys(payload || {}).forEach(k => {
      const v = payload[k];
      if (v === null || v === undefined) return;
      clean[k] = typeof v === "string" ? v.slice(0, 200) : v;
    });
    api.post("/plus/track", { event, properties: clean }).catch(() => {});
  } catch { /* analytics must never break the page */ }
}

/**
 * Small state machine wrapping MediaRecorder. Consumers call:
 *   const rec = createRecorder({ onStop: (blob) => {...} });
 *   await rec.start();      // requests mic + starts recording
 *   rec.stop();              // stops; onStop fires with the recorded Blob
 *   rec.cancel();            // stops + discards; onStop is NOT called
 *
 * `start()` throws with an object { code: "permission_denied" | ... }
 * so the caller can render a graceful message and hide the mic.
 */
export function createRecorder({ onStop, maxSeconds = 180 } = {}) {
  let stream = null;
  let recorder = null;
  let chunks = [];
  let mime = null;
  let cancelled = false;
  let startedAt = 0;
  let timeoutId = null;

  async function start() {
    if (!isVoiceCapable()) {
      throw { code: "unsupported" };
    }
    mime = _pickMime();
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err && err.name ? String(err.name) : "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        throw { code: "permission_denied" };
      }
      if (name === "NotFoundError" || name === "OverconstrainedError") {
        throw { code: "no_microphone" };
      }
      throw { code: "unknown_start_error", detail: String(err) };
    }
    chunks = [];
    cancelled = false;
    startedAt = performance.now();
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
    } catch {
      recorder = new MediaRecorder(stream);
    }
    recorder.addEventListener("dataavailable", ev => {
      if (ev.data && ev.data.size) chunks.push(ev.data);
    });
    recorder.addEventListener("stop", () => {
      const ms = Math.max(0, Math.round(performance.now() - startedAt));
      // Free the mic immediately so the browser indicator disappears.
      try { stream.getTracks().forEach(t => t.stop()); } catch { /* noop */ }
      if (cancelled) return;
      const blobType = mime || (chunks[0] && chunks[0].type) || "audio/webm";
      const blob = new Blob(chunks, { type: blobType });
      onStop && onStop({ blob, mime: blobType, durationMs: ms });
    });
    recorder.start();
    // Hard cap on recording length.
    timeoutId = setTimeout(() => {
      if (recorder && recorder.state === "recording") stop();
    }, Math.max(1000, maxSeconds * 1000));
  }

  function stop() {
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    if (!recorder) return;
    if (recorder.state === "recording") recorder.stop();
  }

  function cancel() {
    cancelled = true;
    stop();
  }

  return { start, stop, cancel };
}
